import { normalizeDigits } from "@/lib/decimal-amount";
import { readFacetText } from "@/lib/browse/facet-text";

/**
 * THE ITEMS TAB'S FILTERS — what `/nfts` accepts beside `name` and the order,
 * kept in the page's URL under the API's own names so a copied link opens the
 * same list:
 *
 *   listed=1|0         on sale / not on sale
 *   price_min, price_max, currency_id
 *   has_offers=1       with at least one open offer
 *   on_auction=1       in an auction
 *   t.<trait>=<value>  one per chosen value; the same trait twice is OR'd by
 *                      the backend, two traits are AND'd
 *
 * The older single `?trait=&value=` pair (the NFT page's trait links) is read
 * as one chosen value; any change to the traits rewrites it as `t.` keys.
 * Every value is shape-checked here, so nothing unchecked reaches a request.
 */

export type TraitPick = { trait: string; value: string };

export type ItemFilters = {
  listed: "1" | "0" | null;
  priceMin: string | null;
  priceMax: string | null;
  currencyId: number | null;
  hasOffers: boolean;
  onAuction: boolean;
  traits: TraitPick[];
};

export const NO_ITEM_FILTERS: ItemFilters = {
  listed: null, priceMin: null, priceMax: null, currencyId: null, hasOffers: false, onAuction: false, traits: [],
};

const TRAIT_PREFIX = "t.";
/** A cap on the chosen values — the request stays a sane length. */
export const MAX_TRAIT_PICKS = 20;

/** A non-negative decimal as the backend reads it (Persian digits accepted), or null. */
export function readPrice(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const text = normalizeDigits(String(raw)).trim();
  if (!/^\d{1,18}(\.\d{1,18})?$/.test(text)) return null;
  return text;
}

function readId(raw: string | null): number | null {
  if (!raw || !/^\d{1,9}$/.test(raw)) return null;
  const n = Number(raw);
  return n > 0 ? n : null;
}

export function readItemFilters(sp: URLSearchParams): ItemFilters {
  const listedRaw = sp.get("listed");
  const traits: TraitPick[] = [];
  const seen = new Set<string>();
  const add = (trait: string | null, value: string | null) => {
    if (!trait || !value || traits.length >= MAX_TRAIT_PICKS) return;
    const key = `${trait}\u0000${value}`;
    if (seen.has(key)) return;
    seen.add(key);
    traits.push({ trait, value });
  };
  add(readFacetText(sp.get("trait")), readFacetText(sp.get("value")));
  sp.forEach((value, key) => {
    if (key.startsWith(TRAIT_PREFIX)) add(readFacetText(key.slice(TRAIT_PREFIX.length)), readFacetText(value));
  });
  return {
    listed: listedRaw === "1" || listedRaw === "0" ? listedRaw : null,
    priceMin: readPrice(sp.get("price_min")),
    priceMax: readPrice(sp.get("price_max")),
    currencyId: readId(sp.get("currency_id")),
    hasOffers: sp.get("has_offers") === "1",
    onAuction: sp.get("on_auction") === "1",
    traits,
  };
}

/** Write a change onto the current query; untouched keys stay as they are. */
export function writeItemFilters(current: URLSearchParams, patch: Partial<ItemFilters>): URLSearchParams {
  const next = new URLSearchParams(current);
  const put = (key: string, value: string | null | undefined) => {
    if (value === undefined) return;
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
  };
  if ("listed" in patch) put("listed", patch.listed ?? null);
  if ("priceMin" in patch) put("price_min", patch.priceMin ?? null);
  if ("priceMax" in patch) put("price_max", patch.priceMax ?? null);
  if ("currencyId" in patch) put("currency_id", patch.currencyId ? String(patch.currencyId) : null);
  if ("hasOffers" in patch) put("has_offers", patch.hasOffers ? "1" : null);
  if ("onAuction" in patch) put("on_auction", patch.onAuction ? "1" : null);
  if (patch.traits) {
    next.delete("trait");
    next.delete("value");
    Array.from(next.keys()).filter((k) => k.startsWith(TRAIT_PREFIX)).forEach((k) => next.delete(k));
    patch.traits.slice(0, MAX_TRAIT_PICKS).forEach((p) => next.append(`${TRAIT_PREFIX}${p.trait}`, p.value));
  }
  // A list that changed shape has no page 3 to stay on.
  next.delete("page");
  return next;
}

/** Every key this module owns — cleared together by «clear all». */
export function isItemFilterKey(key: string) {
  return ["listed", "price_min", "price_max", "currency_id", "has_offers", "on_auction", "trait", "value"].includes(key) || key.startsWith(TRAIT_PREFIX);
}

/** Choose or drop one value. */
export function toggleTrait(picks: TraitPick[], pick: TraitPick): TraitPick[] {
  const on = picks.some((p) => p.trait === pick.trait && p.value === pick.value);
  return on ? picks.filter((p) => !(p.trait === pick.trait && p.value === pick.value)) : [...picks, pick].slice(0, MAX_TRAIT_PICKS);
}

/** What `/nfts` is sent. */
export function itemFilterParams(f: ItemFilters): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.listed) out.listed = f.listed;
  if (f.priceMin) out.price_min = f.priceMin;
  if (f.priceMax) out.price_max = f.priceMax;
  if (f.currencyId) out.currency_id = String(f.currencyId);
  if (f.hasOffers) out.has_offers = "1";
  if (f.onAuction) out.on_auction = "1";
  if (f.traits.length === 1) {
    out.trait = f.traits[0].trait;
    out.value = f.traits[0].value;
  } else {
    f.traits.forEach((p, i) => {
      out[`traits[${i}][trait]`] = p.trait;
      out[`traits[${i}][value]`] = p.value;
    });
  }
  return out;
}

export function countItemFilters(f: ItemFilters) {
  return (f.listed ? 1 : 0) + (f.priceMin || f.priceMax ? 1 : 0) + (f.currencyId ? 1 : 0) + (f.hasOffers ? 1 : 0) + (f.onAuction ? 1 : 0) + f.traits.length;
}

/**
 * The orders `/nfts` takes for a collection's works. Kept here, not in the
 * shared browse schema: price, likes and listing date exist on this endpoint
 * only. `?sort=` carries the key; the default writes nothing.
 */
export const ITEM_SORT_PRESETS = {
  newest: { orderBy: "id", order: "desc" },
  oldest: { orderBy: "id", order: "asc" },
  "price-asc": { orderBy: "price", order: "asc" },
  "price-desc": { orderBy: "price", order: "desc" },
  "likes-desc": { orderBy: "likes", order: "desc" },
  "listed-desc": { orderBy: "listed_at", order: "desc" },
  "name-asc": { orderBy: "name", order: "asc" },
  "name-desc": { orderBy: "name", order: "desc" },
} as const;

export type ItemSort = keyof typeof ITEM_SORT_PRESETS;
export const ITEM_SORTS = Object.keys(ITEM_SORT_PRESETS) as ItemSort[];

export function readItemSort(raw: string | null): ItemSort {
  return raw && Object.prototype.hasOwnProperty.call(ITEM_SORT_PRESETS, raw) ? (raw as ItemSort) : "newest";
}
